/* Pocket Up — organized side tabs + new views (reserve / persons / cheques / AI / savings) */
import React, { useState } from 'react';
import {
  X, Home, Activity, History as HistoryIcon, Wallet, Landmark, UserRound,
  Receipt, Sparkles, PiggyBank, CheckCircle2, AlertTriangle, Loader2, TrendingUp,
} from 'lucide-react';
import { toast } from 'sonner';
import { fmtMoney, TxRow } from './PocketHome';
import { PocketBrand } from './PocketLogo';
import { financeApi } from '../../lib/api';

export const POCKET_TABS = [
  { id: 'home', label: 'الرئيسية', icon: Home, group: 'الأساسية' },
  { id: 'insights', label: 'تحليلات الأسبوع', icon: Activity, group: 'الأساسية' },
  { id: 'history', label: 'سجل الحركات', icon: HistoryIcon, group: 'الأساسية' },
  { id: 'savings', label: 'التوفير 30%', icon: PiggyBank, group: 'المحافظ' },
  { id: 'reserve', label: 'احتياطي 20%', icon: Landmark, group: 'المحافظ' },
  { id: 'person_ismail', label: 'شخصي — إسماعيل', icon: UserRound, group: 'المحافظ' },
  { id: 'person_rafat', label: 'شخصي — رأفت', icon: UserRound, group: 'المحافظ' },
  { id: 'cheques', label: 'الشيكات — مدفوع / غير مدفوع', icon: Receipt, group: 'الأدوات' },
  { id: 'ai', label: 'فاحص AI', icon: Sparkles, group: 'الأدوات' },
  { id: 'wallet', label: 'المحفظة والإعدادات', icon: Wallet, group: 'الأدوات' },
];

export const PocketDrawer = ({ open, tab, onPick, onClose, user }) => {
  let lastGroup = null;
  return (
    <>
      {open && <div className="pk-backdrop" onClick={onClose} data-testid="pocket-drawer-backdrop" />}
      <div className={`pk-drawer ${open ? 'open' : ''}`} data-testid="pocket-drawer">
        <div className="pk-drawer-head">
          <div>
            <PocketBrand markSize={34} wordSize={13} />
            <div style={{ fontSize: 13, color: 'var(--pk-mut)', marginTop: 8, fontWeight: 600 }}>{user?.name || 'أهلًا'}</div>
          </div>
          <button className="pk-drawer-x" onClick={onClose} data-testid="pocket-drawer-close"><X size={18} /></button>
        </div>
        {POCKET_TABS.map((t) => {
          const showGroup = t.group !== lastGroup;
          lastGroup = t.group;
          return (
            <React.Fragment key={t.id}>
              {showGroup && <div className="pk-drawer-group">{t.group}</div>}
              <button className={`pk-drawer-item ${tab === t.id ? 'active' : ''}`}
                onClick={() => { onPick(t.id); onClose(); }} data-testid={`pocket-tab-${t.id}`}>
                <span className="ic"><t.icon size={17} strokeWidth={2} /></span>
                <span>{t.label}</span>
              </button>
            </React.Fragment>
          );
        })}
      </div>
    </>
  );
};

const Head = ({ tag, title }) => (
  <div className="pk-topbar">
    <div>
      <div className="pk-greet">{tag}</div>
      <div className="pk-uname">{title}</div>
    </div>
  </div>
);

/* ── SAVINGS 30% ─────────────────────────────────────────────────────── */
export const SavingsView = ({ summary }) => {
  const tm = summary?.this_month || {};
  const income = Number(tm.income) || 0;
  const expenses = Number(tm.expenses) || 0;
  const saved = Math.max(0, income - expenses);
  const target = income * 0.3;
  const pct = target > 0 ? Math.min(100, Math.round((saved / target) * 100)) : 0;
  const onTrack = income > 0 && saved >= target;
  const months = (summary?.monthly || []).slice(-6);
  return (
    <div className="pk-view" data-testid="pocket-savings-view">
      <Head tag="Savings engine" title="آلية التوفير — هدف 30% شهريًا" />
      <div style={{ padding: '0 20px' }}>
        <div className={`pk-card ${onTrack ? 'gold' : ''}`} style={{ padding: '18px 18px 20px' }} data-testid="savings-main-card">
          <div className="pk-card-orb" />
          <div className="pk-card-tag"><PiggyBank size={12} /> هذا الشهر</div>
          <div className="pk-balval" style={{ fontSize: 34, ...(onTrack ? {} : { color: 'var(--pk-amber)' }) }}>
            <span dir="ltr">{fmtMoney(saved)}</span>
          </div>
          <div className="pk-balsub">الموفَّر بعد كل النفقات · الهدف <b dir="ltr">{fmtMoney(target)}</b> (30% من الدخل)</div>
          <div className="pk-progress"><div className="pk-progress-fill" style={{ width: `${pct}%`, background: onTrack ? 'var(--pk-green)' : 'var(--pk-amber)' }} /></div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, color: 'var(--pk-mut)', marginTop: 6 }}>
            <span data-testid="savings-pct">{pct}% من الهدف</span>
            <span dir="ltr">دخل {fmtMoney(income)} − صرف {fmtMoney(expenses)}</span>
          </div>
        </div>
        <div className={`pk-note ${onTrack ? 'ok' : 'warn'}`} data-testid="savings-status-note">
          {income === 0
            ? 'ما في دخل مسجّل هالشهر بعد — سجّل الدخل ليشتغل حساب التوفير'
            : onTrack
              ? `👏 ممتاز! وفرتوا ${fmtMoney(saved)} — أعلى من هدف الـ30%. كمّلوا هيك`
              : `⚠️ ناقصكم ${fmtMoney(Math.max(0, target - saved))} لتوصلوا هدف التوفير 30% هالشهر — خففوا الصرف`}
        </div>
      </div>
      <div className="pk-sect">آخر 6 أشهر — نسبة التوفير</div>
      <div style={{ padding: '0 20px' }}>
        {months.map((m) => {
          const s = Math.max(0, (m.income || 0) - (m.expenses || 0));
          const p = m.income > 0 ? Math.round((s / m.income) * 100) : 0;
          const ok = p >= 30;
          return (
            <div key={m.month} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 2px' }}>
              <span className="pk-num" style={{ fontSize: 11, width: 56, color: 'var(--pk-mut)' }} dir="ltr">{m.month}</span>
              <div style={{ flex: 1, height: 7, borderRadius: 4, background: 'var(--pk-track)', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${Math.min(100, p)}%`, borderRadius: 4, background: ok ? 'var(--pk-green)' : 'var(--pk-amber)' }} />
              </div>
              <span className="pk-num" style={{ fontSize: 12, fontWeight: 800, width: 42, textAlign: 'left', color: ok ? 'var(--pk-green)' : 'var(--pk-amber)' }} dir="ltr">{p}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ── RESERVE 20% ─────────────────────────────────────────────────────── */
export const ReserveView = ({ wallets, ledger }) => {
  const entries = ledger?.entries || [];
  return (
    <div className="pk-view" data-testid="pocket-reserve-view">
      <Head tag="Reserve 20%" title="احتياطي الشغل" />
      <div style={{ padding: '0 20px' }}>
        <div className="pk-card mag" style={{ padding: '17px 18px 19px' }}>
          <div className="pk-card-orb" />
          <div className="pk-card-tag"><Landmark size={12} /> الرصيد الحالي</div>
          <div className="pk-balval" style={{ fontSize: 32 }}><span dir="ltr">{fmtMoney(wallets?.reserve?.balance)}</span></div>
          <div style={{ display: 'flex', gap: 16, marginTop: 10, fontSize: 11.5, color: 'var(--pk-mut)' }}>
            <span>محجوز: <b className="pk-num" dir="ltr">{fmtMoney(wallets?.reserve?.credited)}</b></span>
            <span>مصروف: <b className="pk-num" dir="ltr">{fmtMoney(wallets?.reserve?.spent)}</b></span>
          </div>
        </div>
      </div>
      <div className="pk-sect">سجل الاقتطاعات (لكل دخل)</div>
      <div style={{ padding: '0 20px' }}>
        {entries.length === 0 && <div className="pk-empty">لا اقتطاعات بعد — كل دخل جديد ينحجز منه 20% تلقائيًا</div>}
        {entries.slice(0, 30).map((e, i) => (
          <div key={i} className="pk-lrow" data-testid={`reserve-entry-${i}`}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13.5, fontWeight: 800 }}>{e.source || e.description || e.category || 'دخل'}</div>
              <div style={{ fontSize: 11, color: 'var(--pk-mut)', marginTop: 2 }}>{(e.date || '').slice(0, 10)} · دخل <span dir="ltr" className="pk-num">{fmtMoney(e.income_usd ?? e.amount_usd ?? e.amount)}</span></div>
            </div>
            <div className="pk-num" style={{ fontWeight: 800, color: 'var(--pk-mag)' }} dir="ltr">+{fmtMoney(e.reserve_cut ?? e.cut ?? e.reserved)}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ── PERSONAL (per person) ───────────────────────────────────────────── */
const matchPerson = (person, keys) => {
  const p = (person || '').toLowerCase().replace(/[إأآ]/g, 'ا');
  return keys.some((k) => p.includes(k));
};

export const PersonView = ({ label, keys, recent, summary }) => {
  const txs = (recent || []).filter((t) => t.type === 'personal' && matchPerson(t.person, keys));
  const total = txs.reduce((s, t) => s + (Number(t.amount_usd || t.amount) || 0), 0);
  const thisMonth = new Date().toISOString().slice(0, 7);
  const monthTotal = txs.filter((t) => ((t.date || t.created_at || '').slice(0, 7) === thisMonth))
    .reduce((s, t) => s + (Number(t.amount_usd || t.amount) || 0), 0);
  const allTimeFromSummary = Object.entries(summary?.personal_by_person || {})
    .filter(([k]) => matchPerson(k, keys)).reduce((s, [, v]) => s + v, 0);
  return (
    <div className="pk-view" data-testid={`pocket-person-view-${keys[0]}`}>
      <Head tag="Personal" title={`مصاريف ${label}`} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, padding: '0 20px' }}>
        <div className="pk-card gold" style={{ padding: '14px 15px 16px' }}>
          <div className="pk-card-tag">هذا الشهر</div>
          <div className="pk-balval" style={{ fontSize: 24 }}><span dir="ltr">{fmtMoney(monthTotal)}</span></div>
        </div>
        <div className="pk-card" style={{ padding: '14px 15px 16px' }}>
          <div className="pk-card-tag">الإجمالي الكلي</div>
          <div className="pk-balval" style={{ fontSize: 24 }}><span dir="ltr">{fmtMoney(allTimeFromSummary || total)}</span></div>
        </div>
      </div>
      <div className="pk-sect">حركات {label}</div>
      {txs.length === 0
        ? <div className="pk-empty">لا مصاريف مسجلة باسم {label} بعد — بالتسجيل الصوتي قول «شخصي {label}»</div>
        : txs.map((t) => (
          <TxRow key={t.id} t={t} meta={`${new Date(t.date || t.created_at).toLocaleDateString('en-GB')} · ${t.category || ''}`} />
        ))}
    </div>
  );
};

/* ── CHEQUES paid / unpaid ───────────────────────────────────────────── */
export const ChequesView = ({ cheques, onChanged }) => {
  const [seg, setSeg] = useState('unpaid');
  const [busyId, setBusyId] = useState(null);
  const list = cheques || [];
  const unpaid = list.filter((c) => c.status !== 'paid');
  const paid = list.filter((c) => c.status === 'paid');
  const shown = seg === 'unpaid' ? unpaid : paid;
  const today = new Date().toISOString().slice(0, 10);

  const markPaid = async (c) => {
    setBusyId(c.id);
    try {
      await financeApi.updateCheque(c.id, { status: 'paid' });
      toast.success(`تم تسجيل شيك ${c.payee} كمدفوع ✓`);
      onChanged?.();
    } catch { toast.error('فشل التحديث'); } finally { setBusyId(null); }
  };

  return (
    <div className="pk-view" data-testid="pocket-cheques-view">
      <Head tag="Cheques" title="الشيكات والدفعات" />
      <div style={{ padding: '0 20px' }}>
        <div className="pk-seg" style={{ width: '100%' }}>
          <button className={seg === 'unpaid' ? 'on' : ''} onClick={() => setSeg('unpaid')} data-testid="cheques-seg-unpaid" style={{ flex: 1 }}>
            غير مدفوع ({unpaid.length})
          </button>
          <button className={seg === 'paid' ? 'on' : ''} onClick={() => setSeg('paid')} data-testid="cheques-seg-paid" style={{ flex: 1 }}>
            مدفوع ({paid.length})
          </button>
        </div>
        <div style={{ marginTop: 14 }}>
          {shown.length === 0 && <div className="pk-empty">{seg === 'unpaid' ? 'ما في شيكات مستحقة 🎯' : 'ما في شيكات مدفوعة بعد'}</div>}
          {shown.map((c) => {
            const overdue = c.status !== 'paid' && c.due_date && c.due_date < today;
            return (
              <div key={c.id} className="pk-card" style={{ padding: '13px 15px', marginBottom: 10 }} data-testid={`cheque-${c.id}`}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14.5, fontWeight: 800 }}>{c.payee}</div>
                    <div style={{ fontSize: 11.5, color: overdue ? 'var(--pk-red)' : 'var(--pk-mut)', marginTop: 2 }}>
                      {overdue && <AlertTriangle size={11} style={{ verticalAlign: '-1px' }} />} استحقاق {c.due_date} {c.purpose ? `· ${c.purpose}` : ''}
                    </div>
                  </div>
                  <div className="pk-num" style={{ fontSize: 16, fontWeight: 800 }} dir="ltr">{fmtMoney(c.amount_usd || c.amount)}</div>
                </div>
                {c.status !== 'paid' && (
                  <button className="pk-btn" style={{ marginTop: 10, padding: '9px 0', fontSize: 13 }}
                    onClick={() => markPaid(c)} disabled={busyId === c.id} data-testid={`cheque-markpaid-${c.id}`}>
                    {busyId === c.id ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} style={{ verticalAlign: '-2px' }} />} تم الدفع ✓
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/* ── AI CHECKER ──────────────────────────────────────────────────────── */
export const AICheckerView = ({ summary }) => {
  const [advice, setAdvice] = useState(null);
  const [busy, setBusy] = useState(false);
  const tm = summary?.this_month || {};
  const income = Number(tm.income) || 0;
  const expenses = Number(tm.expenses) || 0;
  const ratio = income > 0 ? Math.round((expenses / income) * 100) : null;
  const over = income > 0 && expenses > income * 0.7;
  const critical = income > 0 && expenses > income;

  const run = async () => {
    setBusy(true);
    try {
      const r = await financeApi.aiAdvice();
      setAdvice(r.data.advice);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فاحص AI غير متاح حاليًا');
    } finally { setBusy(false); }
  };

  return (
    <div className="pk-view" data-testid="pocket-ai-view">
      <Head tag="AI spend checker" title="فاحص المصاريف الذكي" />
      <div style={{ padding: '0 20px' }}>
        {ratio !== null && (
          <div className={`pk-note ${critical ? 'bad' : over ? 'warn' : 'ok'}`} data-testid="ai-overspend-banner">
            {critical
              ? `🚨 انتبهوا! صرفكم هالشهر (${fmtMoney(expenses)}) أعلى من دخلكم (${fmtMoney(income)}) — بتصرفوا زيادة عن اللزوم!`
              : over
                ? `⚠️ بتصرفوا زيادة عن اللزوم — صرفتوا ${ratio}% من دخل الشهر. الهدف يظل تحت 70% لتوفروا 30%`
                : `✅ وضعكم ممتاز — صرفكم ${ratio}% من الدخل وضمن الحد الصحي`}
          </div>
        )}
        <div className="pk-card" style={{ padding: '16px 17px', marginTop: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Sparkles size={20} style={{ color: 'var(--pk-gold)' }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14.5, fontWeight: 800 }}>تحليل كامل بالذكاء الاصطناعي</div>
              <div style={{ fontSize: 11.5, color: 'var(--pk-mut)', marginTop: 2 }}>يفحص الدخل والمصاريف والشيكات ويعطيك خطة توفير</div>
            </div>
          </div>
          <button className="pk-btn" style={{ marginTop: 12 }} onClick={run} disabled={busy} data-testid="ai-run-btn">
            {busy ? <><Loader2 size={15} className="animate-spin" style={{ verticalAlign: '-3px' }} /> الذكاء الاصطناعي يفحص أرقامكم…</> : 'افحص مصاريفنا الآن'}
          </button>
        </div>
        {advice && (
          <div className="pk-card" style={{ padding: '16px 17px', marginTop: 12 }} data-testid="ai-advice-card">
            <div className="pk-card-tag"><TrendingUp size={12} /> تقرير الفاحص</div>
            <div style={{ fontSize: 13.5, lineHeight: 1.9, whiteSpace: 'pre-wrap', marginTop: 8 }} dir="auto">{advice}</div>
          </div>
        )}
      </div>
    </div>
  );
};
